import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import type { D4HConfig, D4HQualification, D4HQualMappingEntry, D4HRegion, D4HRosterMatch, D4HSyncSummary } from '@/types'

interface D4HTeam {
  team_id: number
  team_name: string
}

// D4H admin operations. These go through FastAPI (the token never reaches the client),
// so unlike the rest of the app they need a network connection.
export const useD4HStore = defineStore('d4h', () => {
  const config = ref<D4HConfig | null>(null)
  const qualifications = ref<D4HQualification[]>([])
  // Populated when a token can access several D4H teams and one must be picked
  const teamChoices = ref<D4HTeam[]>([])
  const syncing = ref(false)

  async function _api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await useAuthStore().currentUser?.getIdToken()
    const res = await fetch(`/api/d4h${path}`, {
      ...init,
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(body?.detail ?? body?.error?.message ?? `D4H request failed: ${res.status}`)
    return body.data as T
  }

  async function loadConfig() {
    config.value = await _api<D4HConfig>('/config')
  }

  async function saveConfig(input: {
    region: D4HRegion
    api_token?: string
    team_id?: number | null
    subdomain?: string
  }): Promise<{ needsTeam: boolean }> {
    const result = await _api<D4HConfig & { teams: D4HTeam[]; needs_team: boolean }>('/config', {
      method: 'PUT',
      body: JSON.stringify(input),
    })
    const { teams, needs_team, ...rest } = result
    teamChoices.value = needs_team ? teams : []
    config.value = rest
    return { needsTeam: needs_team }
  }

  async function loadQualifications() {
    const data = await _api<{ qualifications: D4HQualification[] }>('/qualifications')
    qualifications.value = data.qualifications
  }

  async function saveQualMapping(qualMapping: Record<string, D4HQualMappingEntry>) {
    const data = await _api<{ qual_mapping: Record<string, D4HQualMappingEntry> }>('/qual-mapping', {
      method: 'PUT',
      body: JSON.stringify({ qual_mapping: qualMapping }),
    })
    if (config.value) config.value = { ...config.value, qual_mapping: data.qual_mapping }
  }

  // The signed-in person's own roster record in an agency, if any. Works before they have a profile.
  async function fetchRosterMatch(agencyId: string): Promise<D4HRosterMatch | null> {
    return _api<D4HRosterMatch | null>(`/roster-match?agency_id=${encodeURIComponent(agencyId)}`)
  }

  async function sync(): Promise<D4HSyncSummary> {
    syncing.value = true
    try {
      const summary = await _api<D4HSyncSummary>('/sync', { method: 'POST' })
      await loadConfig()
      return summary
    } finally {
      syncing.value = false
    }
  }

  return {
    config, qualifications, teamChoices, syncing,
    loadConfig, saveConfig, loadQualifications, saveQualMapping, fetchRosterMatch, sync,
  }
})
